import { projectTimezone } from '@dashboard/utils/projectTimezone'
import { defineStore } from 'pinia'
import { computed, onScopeDispose } from 'vue'
import { useConfigStore } from '@dashboard/stores/configStore'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { makeId, idPrefixes } from '@dashboard/services/uuid'
import { dimensionSchema, questionSchema, type Dimension, type Question } from '@dashboard/types/config'
import api from '@dashboard/services/axiosApi'
import { useReportStore } from '@dashboard/modules/dashboard/stores/reportStore'
import { funnelDefinitionForEachFlow } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { useCampaignStore } from '@dashboard/modules/personalization/stores/campaignStore'
import { logicDefinitionForEachSegmentGroup } from '@dashboard/modules/segment/services/segmentUtilities'
import { usePhraseStore } from '@dashboard/modules/segment/stores/phraseStore'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { ref, watchEffect } from 'vue'
import { getSegmentGroupDeleteImpact, removeQuestionFormFields } from '../services/questionActions'

export const useSegmentGroupStore = defineStore('segmentGroup', () => {
  const configStore = useConfigStore()
  const { config, currentTeamPid } = storeToRefs(configStore)
  const reportStore = useReportStore()
  const { dateRange } = storeToRefs(reportStore)
  const audienceBreakdowns = ref<Record<string, number>>({})
  const metricsConsumers = ref(0)
  const metricsEnabled = computed(() => metricsConsumers.value > 0 && !!currentTeamPid.value)

  function useMetrics() {
    metricsConsumers.value++
    onScopeDispose(() => {
      metricsConsumers.value--
    })
  }

  const segmentGroups = computed(() =>
    [...(config.value?.dimensions || [])].sort((a, b) => a.name.localeCompare(b.name))
  )
  const getById = (id: string) => segmentGroups.value.find((segmentGroup) => segmentGroup.id === id)

  const segmentGroupForSegmentId = (id: string) =>
    segmentGroups.value.find((sg) => sg.segments.find((s) => s.id === id))

  const segmentForSegmentId = (id: string) => segmentGroupForSegmentId(id)?.segments.find((s) => s.id === id)

  const create = ({
    type,
    segmentGroup,
    question
  }: {
    type: 'segmentgroup' | 'question'
    segmentGroup?: Partial<Dimension>
    question?: Partial<Question>
  }) => {
    const namePrefix = type === 'segmentgroup' ? 'Segment Group' : 'Question'
    const newSegmentGroup = dimensionSchema.parse({
      id: makeId(idPrefixes.DIMENSION),
      name: `New ${namePrefix} ${segmentGroups.value.length + 1}`,
      segments: [],
      signals: [],
      semanticType: 'CUSTOM',
      syncTo: '',
      syncToCustomField: null,
      ...segmentGroup
    }) as Dimension

    if (newSegmentGroup.syncToCustomField && segmentGroup?.syncTo === undefined) {
      const { integratedPartner } = useIntegrationStore()
      if (integratedPartner) {
        newSegmentGroup.syncTo = integratedPartner.name
      }
    }

    config.value?.dimensions.push(newSegmentGroup)

    if (type === 'question') {
      // Defaults apply only when a caller does not provide Other settings.
      const mergedOtherOptions = {
        enabled: false,
        otherLabel: 'Other',
        inputType: 'textarea',
        inputPrompt: 'Please share more details',
        inputPlaceholder: '',
        syncToCustomField: null
      }

      const newQuestion = questionSchema.parse({
        id: makeId(idPrefixes.RIGHT_ASK_QUESTION),
        dimensionId: newSegmentGroup.id,
        text: question?.text || `Ask about ${newSegmentGroup.name.toLocaleLowerCase()}`,
        displayType: 'buttons',
        isRandomizingOptionsOrder: false,
        isQuestion: true, // Always set to true for questions created through this flow
        ...question,
        otherOptions: question?.otherOptions === undefined ? mergedOtherOptions : question.otherOptions
      }) as Question

      config.value?.rightAsk?.questions.push(newQuestion)
    }

    void recordAnalyticsEvent(
      type === 'question' ? AnalyticsEventTypes.QUESTION_CREATED : AnalyticsEventTypes.SEGMENT_GROUP_CREATED,
      {
        segmentGroupId: newSegmentGroup.id,
        segmentGroupName: newSegmentGroup.name,
        questionText: type === 'question' ? question?.text : undefined,
        hasSyncEnabled: !!newSegmentGroup.syncTo
      }
    )

    return newSegmentGroup
  }

  const destroy = (segmentGroup: Dimension) => {
    if (!config.value) {
      return
    }
    const foundIndex = config.value.dimensions.findIndex((sg) => sg.id === segmentGroup.id)
    if (foundIndex === -1) {
      return
    } // this should really never happen
    const impact = getSegmentGroupDeleteImpact(config.value, segmentGroup.id)
    if (Object.values(impact.blockedBy).some((uses) => uses.length)) {
      throw new Error(impact.message)
    }
    removeQuestionFormFields(config.value, [segmentGroup.id])

    funnelDefinitionForEachFlow((node) => {
      if (node.type === 'sg' && node.segmentGroupId === segmentGroup.id) {
        node.segmentGroupId = null
        // Also clear router split paths that reference this dimension's segments
        if (node.split && Array.isArray(node.split)) {
          node.split = false
        }
      }
      // Clean up set_segment actions referencing this dimension
      if (node.type === 'data' && node.actions) {
        node.actions = node.actions.filter(
          (a: any) => !(a.type === 'set_segment' && a.segmentGroupId === segmentGroup.id)
        )
      }
    })

    logicDefinitionForEachSegmentGroup((condition, nuke) => {
      if (condition.$source === 'segments' && condition.$type === segmentGroup.id) {
        nuke()
      }
    })

    const campaignStore = useCampaignStore()
    campaignStore.campaigns.forEach((campaign) => {
      if (campaign.dimension_id === segmentGroup.id) {
        campaignStore.destroy(campaign)
      }
    })

    const questionStore = useQuestionStore()
    questionStore.questions.forEach((question) => {
      if (question.dimensionId === segmentGroup.id) {
        questionStore.destroy(question)
      }
    })

    const phraseStore = usePhraseStore()
    phraseStore.phrases.forEach((phrase) => {
      if (phrase.segmentGroupId === segmentGroup.id) {
        phraseStore.destroy(phrase)
      }
    })

    // Track segment group deletion
    void recordAnalyticsEvent(AnalyticsEventTypes.SEGMENT_GROUP_DELETED, {
      segmentGroupId: segmentGroup.id,
      segmentGroupName: segmentGroup.name
    })

    config.value?.dimensions?.splice(foundIndex, 1)
  }

  const audienceBreakdownQuery = useQuery({
    queryKey: ['audienceBreakdown', currentTeamPid, dateRange],
    queryFn: () => {
      if (currentTeamPid.value === null) {
        throw new Error('A team is required to load audience breakdowns')
      }
      return fetchAudienceBreakdown(String(currentTeamPid.value))
    },
    enabled: metricsEnabled
  })

  watchEffect(() => {
    if (audienceBreakdownQuery.data.value) {
      audienceBreakdowns.value = audienceBreakdownQuery.data.value.segmentCounts
    }
  })

  const fetchAudienceBreakdown = async (teamPid: string) =>
    api
      .get(`/api/${teamPid}/metrics/dashboardAudienceBreakdown`, {
        params: {
          start: dateRange.value.start,
          end: dateRange.value.end
        }
      })
      .then((res) => res.data)

  // Segment engagement metrics (pages/visitor per segment with lift vs baseline)
  const segmentEngagement = ref<Record<string, any>>({})

  const segmentEngagementQuery = useQuery({
    queryKey: ['segmentEngagement', currentTeamPid, dateRange],
    queryFn: () => {
      if (currentTeamPid.value === null) {
        throw new Error('A team is required to load segment engagement')
      }
      return fetchSegmentEngagement(String(currentTeamPid.value))
    },
    enabled: metricsEnabled
  })

  watchEffect(() => {
    if (segmentEngagementQuery.data.value) {
      // Index engagement data by segmentId for fast lookups
      const indexed: Record<string, any> = {}
      for (const dim of segmentEngagementQuery.data.value.dimensions || []) {
        for (const seg of dim.segments || []) {
          indexed[seg.segmentId] = seg
        }
      }
      segmentEngagement.value = indexed
    }
  })

  const fetchSegmentEngagement = async (teamPid: string) =>
    api
      .get(`/api/${teamPid}/metrics/segmentEngagement`, {
        params: {
          basis: 'engagement',
          start: dateRange.value.start,
          end: dateRange.value.end,
          timezone: dateRange.value.timezone ?? projectTimezone(),
          includeLeaderboard: false
        }
      })
      .then((res) => res.data)

  /**
   * Get engagement lift data for a segment.
   * Returns null only if there's truly no data. Confidence filtering is left
   * to the detail page — the card shows directional indicators for all segments
   * with any data to keep the display consistent within a dimension.
   */
  const getEngagementForSegment = (segmentId: string) => {
    const data = segmentEngagement.value[segmentId]
    if (!data) {
      return null
    }
    // Only hide when there's literally no data at all
    if (data.uniqueVisitors === 0) {
      return null
    }
    return data
  }

  /**
   * Check if a dimension has meaningful engagement differentiation.
   * Returns false if all segments show identical visitor counts (no classification).
   */
  const hasEngagementData = (segmentGroupId: string) => {
    const segmentGroup = getById(segmentGroupId)
    if (!segmentGroup) {
      return false
    }

    const segmentData = segmentGroup.segments.map((s) => segmentEngagement.value[s.id]).filter(Boolean)

    if (segmentData.length === 0) {
      return false
    }

    // If all segments have identical engagement values, there's no real differentiation
    const values = segmentData.map((s) => s.value)
    const allSameValue = values.every((v) => v === values[0])
    if (allSameValue) {
      return false
    }

    return true
  }

  const getAudienceBreakdownForId = (segmentGroupId: string) => {
    const segmentGroup = getById(segmentGroupId)
    if (!segmentGroup) {
      return { total: 0, segments: [] }
    }

    const segmentIds = segmentGroup.segments.map((segment) => segment.id)

    const breakdown = segmentIds.reduce<Record<string, number>>((acc, segmentId) => {
      acc[segmentId] = audienceBreakdowns.value[segmentId] || 0
      return acc
    }, {})

    const total = Object.values(breakdown).reduce((sum, count) => sum + count, 0)

    return {
      total,
      segments: Object.entries(breakdown).map(([id, count]) => ({ id, count }))
    }
  }

  return {
    segmentGroups,
    useMetrics,
    getById,
    getAudienceBreakdownForId,
    getEngagementForSegment,
    hasEngagementData,
    segmentGroupForSegmentId,
    segmentForSegmentId,
    audienceBreakdowns,
    isLoadingAudienceBreakdown: audienceBreakdownQuery.isLoading,
    isErrorAudienceBreakdown: audienceBreakdownQuery.isError,
    isLoadingEngagement: segmentEngagementQuery.isLoading,
    isErrorEngagement: segmentEngagementQuery.isError,
    create,
    destroy
  }
})
